import type { GroupedSuggestion, SuggestionOption } from "./types";

// Re-export type guards and value extractors
export * from "./utils/suggestionTypeGuards";
export * from "./utils/suggestionValueExtractor";

/**
 * Helper function to create searchable text from an object
 */
export const createSearchableText = (obj: unknown): string => {
	const extractText = (value: unknown): string[] => {
		if (typeof value === "string") return [value];
		if (typeof value === "number") return [value.toString()];
		if (Array.isArray(value)) return value.flatMap(extractText);
		if (value && typeof value === "object") {
			return Object.values(value).flatMap(extractText);
		}
		return [];
	};

	return extractText(obj).filter(Boolean).join(" ").toLowerCase();
};

/**
 * Filter suggestions based on search input
 */
export const filterSuggestions = (
	suggestions: SuggestionOption[],
	inputValue: string,
): SuggestionOption[] => {
	if (!inputValue.trim()) return suggestions;

	const searchTerms = inputValue.toLowerCase().split(" ").filter(Boolean);

	return suggestions.filter((suggestion) =>
		searchTerms.every(
			(term) =>
				suggestion.searchableText.includes(term) ||
				suggestion.label.toLowerCase().includes(term) ||
				suggestion.category.toLowerCase().includes(term),
		),
	);
};

/**
 * Group suggestions by category
 */
export const groupSuggestionsByCategory = (
	suggestions: SuggestionOption[],
): GroupedSuggestion[] => {
	const groups: { [key: string]: SuggestionOption[] } = {};

	suggestions.forEach((suggestion) => {
		if (!groups[suggestion.category]) {
			groups[suggestion.category] = [];
		}
		groups[suggestion.category].push(suggestion);
	});

	return Object.keys(groups).map((category) => ({
		label: category,
		options: groups[category].sort((a, b) => a.label.localeCompare(b.label)),
	}));
};
